import axiosInstance from '../api';

export type ScenarioKey = 'AGGRESSIVE' | 'BALANCED' | 'CONSERVATIVE';

export interface OfferScenarioTerms {
  price: number;
  earnestMoney: number;
  earnestMoneyPercent: number;
  inspectionPeriod: number;
  closingDays: number;
  paymentMethod: 'Financed' | 'Cash';
  concessions: number;
  contingent: boolean;
}

export interface OfferScenario {
  key: ScenarioKey;
  label: string;
  price: number;
  /** 0-100. */
  winProbability: number;
  tradeOffs: string[];
  terms: OfferScenarioTerms;
}

export interface OfferReasoningSnapshot {
  listPrice: number;
  daysOnMarket: number | null;
  medianDaysOnMarketSold: number | null;
  listToSaleRatio: number | null;
  marketCondition: string | null;
  competingOfferCount: number;
  valueRange: { low: number; high: number } | null;
}

export interface OfferReasoning {
  listingId: string;
  address: string;
  cityStateZip: string;
  snapshot: OfferReasoningSnapshot;
  scenarios: OfferScenario[];
  recommendedKey: ScenarioKey;
  recommendation: {
    scenarioKey: ScenarioKey;
    headline: string;
    evidence: string[];
  };
  buyingPower: {
    /**
     * Offered commission minus Homepoint's flat fee. Can be zero or negative
     * when the fee exceeds the commission, so never present it as savings
     * unless `hasBuyingPower` is true.
     */
    amount: number;
    hasBuyingPower: boolean;
    /**
     * False when the commission is the placeholder a buyer shadow listing is
     * created with rather than compensation Homepoint has confirmed. Present
     * an unverified amount as an assumption, never as money already banked.
     */
    commissionVerified: boolean;
    commissionPercent: number;
    commissionAmount: number;
    homepointFee: number;
  };
  defaultTerms: OfferScenarioTerms;
  degraded: boolean;
  degradedReason: string | null;
}

const offerReasoningService = {
  find: async (listingId: string): Promise<OfferReasoning> => {
    const res = await axiosInstance.get('/offer-reasoning', {
      params: { listingId },
    });
    return res.data;
  },
};

export default offerReasoningService;
